import React from 'react';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ITopOverdueData } from 'api/queries/getDelayDriver.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import Icon from 'components/common/Icons/icon';
import { List } from 'components/common/ListWrapper/list';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import { InputOption, ValueContainer } from 'components/common/MyCustomSelect/myCustomSelect';
import NoData from 'components/common/NoData/noData';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import SwitchComponent from 'components/common/Switch/Switch';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { CustomPagination } from 'components/common/pagination/customPagination';
import { TitleWithCounter } from 'components/common/titleWithCounter/titleWithCounter';

import { statusOptions } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramCriticalAnalysis/criticalAnalysisDelayDriversFilter';
import { morefilterItems } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramCriticalAnalysis/utils';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { CustomizedRoundIcon, CustomizedTooltip, MyCustomSelect } from 'components/common';
import { useDelayedActivitiesLogic } from 'components/widgets/DelayedActivities/DelayedActivities.logic';
import { useDelayedActivitiesProvider } from 'components/widgets/DelayedActivities/DelayedActivities.provider';
import {
    TDelayTypeOptions,
    TOP_OVERDUE_OVERRUN_FILTERS,
    columnsV8,
    delayTypeOptions,
    initialState,
} from 'components/widgets/DelayedActivities/DelayedActivities.utils';

const id = 'TopOverdueOverrunActivities';
const componentKey = 'topOverdueOverrunActivities';
const title = 'Delayed Activities';

const DelayedActivities = ({
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
}: IComponentProps) => {
    // Table
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    // Provider
    const { ui, api } = useDelayedActivitiesProvider({
        componentKey,
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        compareVersionId,
        globalFilterData,
        localFilters,
        table,
    });

    // Widget
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    // Logic
    const { logic } = useDelayedActivitiesLogic({
        ui,
        api,
        title,
        subTitle,
        table,
    });

    return (
        <WidgetWithTitle
            id={id}
            title={
                <TitleWithCounter
                    title={title}
                    counter={!logic.notApplicable ? api.getTopOverrunActivities.data?.numOfRows : undefined}
                />
            }
            tooltip={api.getTopOverrunActivities.data?.info}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ) : (
                    <div key={'filters'} className={classes.filters}>
                        <div className={classes.selectWrapper}>
                            <div className={classes.title}>Status</div>
                            <MyCustomSelect<ISelectOption<string>, true>
                                value={ui.state.statusFilter}
                                options={statusOptions}
                                onChange={logic.handleStatusType}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                isMulti={true}
                                placeholder={'Select Status...'}
                                components={{
                                    Option: InputOption,
                                    ValueContainer: ValueContainer,
                                }}
                            />
                        </div>
                        <div>
                            Critical Path Only
                            <SwitchComponent onChange={logic.handleCriticalPath} checked={ui.state.isCriticalPath} />
                        </div>
                        <div className={classes.selectWrapper}>
                            <div className={classes.title}>Delay Type</div>
                            <MyCustomSelect<ISelectOption<TDelayTypeOptions[]>>
                                value={ui.state.delayTypeStatus}
                                options={delayTypeOptions}
                                onChange={logic.handleDelayType}
                                placeholder={'Select Delay Type...'}
                            />
                        </div>
                        <div className={classes.selectWrapper}>
                            <div className={classes.title}>More Filters</div>
                            <MyCustomSelect<ISelectOption<string>, true>
                                value={ui.state.status}
                                options={morefilterItems}
                                onChange={logic.handleStatus}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                isMulti={true}
                                placeholder={'Select More Filters...'}
                                components={{
                                    Option: InputOption,
                                    ValueContainer: ValueContainer,
                                }}
                            />
                        </div>
                        <div className={classes.selectWrapper}>
                            <div className={classes.title}>{`Next:`}</div>
                            <List<string>
                                onChange={logic.handleTimePeriodClick}
                                options={TOP_OVERDUE_OVERRUN_FILTERS}
                                value={ui.state.numOfMonths}
                            />
                        </div>
                    </div>
                ),
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => table.setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    callBack={table.setWidgetColumns}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown
                    key={'export'}
                    title={title}
                    subTitle={subTitle}
                    handleAPICsvExport={logic.handleAPICsvExport}
                />,
            ]}
        >
            <div data-testid={'table-container'} className={classes.tableContainer}>
                {table.updatedInitialState && (
                    <>
                        <TableV8<ITopOverdueData>
                            data={logic.filteredRows}
                            columns={columnsV8}
                            isLoading={api.getTopOverrunActivities.isLoading || api.getTopOverrunActivities.isFetching}
                            noData={
                                <NoData>
                                    {logic.notApplicable
                                        ? 'Not applicable with applied filter'
                                        : 'No activities match the applied filter'}
                                </NoData>
                            }
                            sorting={table.sorting}
                            setSorting={table.setSorting}
                            columnVisibility={table.columnVisibility}
                            setColumnVisibility={table.setColumnVisibility}
                            columnOrder={table.columnOrder}
                            setColumnOrder={table.setColumnOrder}
                            rowSelection={table.rowSelection}
                            setRowSelection={table.setRowSelection}
                        />

                        <div className={classes.pagination}>
                            <CustomPagination
                                totalPages={table.totalPages}
                                pagination={table.pagination}
                                setPagination={table.setPagination}
                                isDisabled={api.getTopOverrunActivities.isFetching}
                            />
                        </div>
                    </>
                )}
            </div>

            <CustomTableColumnsModal
                onHideModal={() => table.setModalColumnsOpen(false)}
                showModal={table.modalColumnsOpen}
                widgetId={widgetId}
                componentKey={componentKey}
                tableInstance={table.tableInstance}
                initialState={initialState}
                columns={columnsV8}
                onChangeOrder={table.setColumnOrder}
                onChangeVisibility={table.setColumnVisibility}
            />
        </WidgetWithTitle>
    );
};

export default React.memo(DelayedActivities);
